import React from "react";
import { formatCurrency, parseInputValue } from "../utils/formatters";
import { calculateMilestoneAssets } from "../utils/calculations";
import "./MilestonesTable.css";

interface MilestonesTableProps {
  income: number;
  dtaMax: number;
  planMTI: number;
  phaseData: any[];
}

const MilestonesTable: React.FC<MilestonesTableProps> = ({
  income,
  dtaMax,
  planMTI,
  phaseData,
}) => {
  const [milestoneData, setMilestoneData] = React.useState(() => {
    return Array.from({ length: 4 }, (_, i) => ({
      yield: (
        phaseData[1]?.dta *
        phaseData[1]?.dscr *
        phaseData[1]?.mti *
        100
      ).toFixed(2),
      intRate: (planMTI * 100).toFixed(2),
      dta: (dtaMax * 100).toFixed(2),
    }));
  });

  const milestoneMetrics = [
    {
      label: "Annual Income Goal",
      key: "income" as const,
      editable: false,
      format: "currency" as const,
    },
    {
      label: "Yield (%)",
      key: "yield" as const,
      editable: true,
      format: "percent" as const,
    },
    {
      label: "Interest Rate (%)",
      key: "intRate" as const,
      editable: true,
      format: "percent" as const,
    },
    {
      label: "DTA (%)",
      key: "dta" as const,
      editable: true,
      format: "percent" as const,
    },
    {
      label: "Total Assets Required",
      key: "totalAssets" as const,
      editable: false,
      format: "currency" as const,
    },
  ];

  const handleCellBlur = (
    milestoneIndex: number,
    field: string,
    value: string
  ) => {
    const numValue = parseInputValue(value);
    setMilestoneData((prev) => {
      const newData = [...prev];
      newData[milestoneIndex] = {
        ...newData[milestoneIndex],
        [field]: numValue.toFixed(2),
      };
      return newData;
    });
  };

  const getCellValue = (
    metric: (typeof milestoneMetrics)[0],
    milestoneIndex: number
  ): string => {
    const milestoneIncome = (income * (milestoneIndex + 1)) / 4;
    const data = milestoneData[milestoneIndex];

    switch (metric.key) {
      case "income":
        return formatCurrency(milestoneIncome);
      case "yield":
        return data.yield;
      case "intRate":
        return data.intRate;
      case "dta":
        return data.dta;
      case "totalAssets":
        const totalAssets = calculateMilestoneAssets(
          milestoneIncome,
          parseFloat(data.yield),
          parseFloat(data.intRate),
          parseFloat(data.dta)
        );
        return formatCurrency(totalAssets);
      default:
        return "";
    }
  };

  const getCellClass = (format: string): string => {
    switch (format) {
      case "currency":
        return "currency";
      case "percent":
        return "percentage";
      case "number":
        return "number";
      default:
        return "";
    }
  };

  const milestoneHeaders = ["PM1", "PM2", "PM3", "GOAL"];
  const milestoneClasses = [
    "milestone-pm1",
    "milestone-pm2",
    "milestone-pm3",
    "milestone-goal",
  ];

  return (
    <div className="section">
      <h2>Milestones to Freedom</h2>
      <p className="section-description">
        This section shows key income milestones and the total assets required
        at each stage to sustainably generate that level of income.
      </p>
      <div className="horizontal-table">
        <table>
          <thead>
            <tr>
              <th className="metric-label">Milestone</th>
              {milestoneHeaders.map((header, index) => (
                <th
                  key={header}
                  className={`phase-header ${milestoneClasses[index]}`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {milestoneMetrics.map((metric) => (
              <tr key={metric.key}>
                <td className="metric-label">{metric.label}</td>
                {Array.from({ length: 4 }, (_, milestoneIndex) => {
                  const cellValue = getCellValue(metric, milestoneIndex);
                  const isEditable = metric.editable;

                  return (
                    <td
                      key={milestoneIndex}
                      className={`data-cell ${getCellClass(metric.format)} ${
                        isEditable ? "editable" : ""
                      }`}
                      contentEditable={isEditable}
                      suppressContentEditableWarning={true}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        if (isEditable) {
                          handleCellBlur(
                            milestoneIndex,
                            metric.key,
                            e.currentTarget.textContent || ""
                          );
                        }
                      }}
                    >
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MilestonesTable;
